// Pure helpers for the shared vendor directory. No Firebase imports: the
// upload, the planner and the suggestion list all lean on these.

const COMPANY_SUFFIXES = new Set(['inc', 'llc', 'co', 'corp', 'corporation', 'company', 'ltd']);

/** The key an upload matches existing vendors on: lowercase, punctuation
 *  gone, trailing company suffixes dropped. "CitroTech Inc." -> "citrotech".
 *  Never empty for a non-empty name. */
export function vendorNameKey(organization: string): string {
  const trimmed = organization.trim();
  // Periods and apostrophes vanish without a gap so "L.L.C." becomes "llc"
  // and "Joe's" becomes "joes"; everything else non-alphanumeric is a gap.
  const words = trimmed
    .toLowerCase()
    .replace(/[.'’]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  while (words.length > 1 && COMPANY_SUFFIXES.has(words[words.length - 1])) words.pop();
  const key = words.join(' ');
  return key || trimmed.toLowerCase();
}

/** Host of a website, lowercase and without "www.". Takes a bare domain or a
 *  full URL. Undefined for anything that isn't one. */
export function websiteDomain(url?: string): string | undefined {
  const raw = (url ?? '').trim();
  // Whitespace or "@" means prose or an email address, not a site.
  if (!raw || /[\s@]/.test(raw)) return undefined;
  try {
    const parsed = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : undefined;
  } catch {
    return undefined;
  }
}

/** '*' marks a vendor who works everywhere. */
export function isNationalVendor(vendor: { areaIds: string[] }): boolean {
  return vendor.areaIds.includes('*');
}

export function vendorCoversArea(vendor: { areaIds: string[] }, areaId: string): boolean {
  return isNationalVendor(vendor) || vendor.areaIds.includes(areaId);
}
